import { api } from './client'

export type SituacaoDoDocumento = 'Rascunho' | 'Publicado' | 'Arquivado'

export type DocumentoNaLista = {
  id: number
  assistenteId: number
  assistente: string
  titulo: string
  situacao: SituacaoDoDocumento
  origem: string
  trechos: number
  criadoEm: string
  atualizadoEm: string | null
}

export type TrechoIndexado = {
  id: number
  ordem: number
  texto: string
  modeloDoVetor: string
  indexadoEm: string
}

export type Documento = {
  id: number
  assistenteId: number
  titulo: string
  conteudo: string
  situacao: SituacaoDoDocumento
  origem: string
  referencia: string | null
  revisadoEm: string | null
  revisadoPor: string | null
  criadoEm: string
  atualizadoEm: string | null

  /** Como o índice guarda — já com a PII mascarada. */
  trechos: TrechoIndexado[]
}

export type TrechoEncontrado = {
  trechoId: number
  documentoId: number
  titulo: string
  texto: string
  origem: string
  pontuacao: number

  /**
   * Posição em cada perna da busca — zero quando aquela perna não encontrou.
   *
   * Estão aqui porque dizem QUAL perna falhou, e a resposta é diferente em cada
   * caso: perna lexical vazia é vocabulário faltando no documento; perna
   * vetorial vazia costuma ser embedding indisponível.
   */
  posicaoLexical: number
  posicaoVetorial: number
}

export type ResultadoDaBusca = { total: number; trechos: TrechoEncontrado[] }

export type ResultadoDaPublicacao = {
  sucesso: boolean
  trechos: number
  piiMascarada: number
  reaproveitado: boolean
  erro: string | null
}

export type PedidoDeDocumento = {
  assistenteId: number
  titulo: string
  conteudo: string
  origem: string
  referencia: string | null
  publicarAgora: boolean
}

export type ResultadoDaImportacao = {
  criados: number
  repetidos: number
  secoes: number
  aviso: string | null
}

const raiz = (marca: string) => `/marcas/${encodeURIComponent(marca)}/conhecimento`

export const conhecimento = {
  listar: (marca: string, assistenteId?: number) =>
    api.get<DocumentoNaLista[]>(
      `${raiz(marca)}${assistenteId ? `?assistenteId=${assistenteId}` : ''}`,
    ),

  ler: (marca: string, id: number) => api.get<Documento>(`${raiz(marca)}/${id}`),

  criar: (marca: string, pedido: PedidoDeDocumento) =>
    api.post<{ id: number; situacao: SituacaoDoDocumento }>(raiz(marca), pedido),

  salvar: (marca: string, id: number, pedido: PedidoDeDocumento) =>
    api.put<{ id: number; situacao: SituacaoDoDocumento; precisaRepublicar: boolean }>(
      `${raiz(marca)}/${id}`,
      pedido,
    ),

  publicar: (marca: string, id: number) =>
    api.post<ResultadoDaPublicacao>(`${raiz(marca)}/${id}/publicar`, {}),

  arquivar: (marca: string, id: number) =>
    api.post<void>(`${raiz(marca)}/${id}/arquivar`, {}),

  buscar: (marca: string, assistenteId: number, pergunta: string) =>
    api.post<ResultadoDaBusca>(`${raiz(marca)}/buscar`, { assistenteId, pergunta }),

  /**
   * Importa um `.md` curado.
   *
   * Vai por `FormData` e **sem** `Content-Type`: o navegador precisa montar o
   * cabeçalho com a fronteira do multipart. Defini-lo à mão quebra o upload de
   * um jeito que só aparece no servidor, como corpo vazio.
   */
  importar: async (marca: string, assistenteId: number, arquivo: File) => {
    const corpo = new FormData()
    corpo.append('arquivo', arquivo)
    corpo.append('assistenteId', String(assistenteId))

    return api.enviarFormulario<ResultadoDaImportacao>(`${raiz(marca)}/importar`, corpo)
  },

  /**
   * O PDF vira **um** documento; o markdown vira vários (um por seção). São
   * portas diferentes de propósito — misturar faria o comportamento depender da
   * extensão do arquivo.
   */
  importarPdf: async (marca: string, assistenteId: number, arquivo: File, titulo: string) => {
    const corpo = new FormData()
    corpo.append('arquivo', arquivo)
    corpo.append('assistenteId', String(assistenteId))
    corpo.append('titulo', titulo)

    return api.enviarFormulario<{
      id: number
      titulo: string
      origem: string
      caracteres: number
      paginas: number
    }>(`${raiz(marca)}/importar-pdf`, corpo)
  },
}
